import { CSSProperties, Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import ReactSelect from 'react-select';
import { customStyles } from '../../../shared/assets/styles/select-styles';
import {
  fetchAllLegalEntities,
  selectLegalEntities,
  selectLegalEntityStatus,
} from '../../../entities/legal-entity/model';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import { LegalEntity, legalEntitySelect } from '../../../types/interfaces';

interface SelectLegalEntitiesProps {
  myLegalEntities?: LegalEntity[];
  selectedLegalEntity: legalEntitySelect | null;
  setSelectedLegalEntity: Dispatch<SetStateAction<legalEntitySelect | null>>;
  manager_id?: number;
  isClearable?: boolean;
  // Точечное переопределение CSS для контрола (внешней «коробки»
  // селекта). Нужно когда вызывающий рендерит селект рядом с инпутом
  // и должен подогнать высоту/радиус/бордер под соседа, не трогая
  // глобальный select-styles (его используют и другие места).
  controlStyleOverrides?: CSSProperties;
}

function SelectLegalEntities({
  myLegalEntities,
  selectedLegalEntity,
  setSelectedLegalEntity,
  manager_id = 0,
  isClearable = true,
  controlStyleOverrides,
}: SelectLegalEntitiesProps) {
  const legalEntities = useAppSelector(selectLegalEntities);
  const legalEntitiesFetchedStatus = useAppSelector(selectLegalEntityStatus);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!myLegalEntities) {
      if (legalEntitiesFetchedStatus !== 'succeeded') {
        dispatch(fetchAllLegalEntities());
      }
    }
  }, [dispatch]);

  const filteredIntegrators = myLegalEntities
    ? myLegalEntities.map((le) => ({ value: le.id, label: le.name }))
    : legalEntities.map((le) => ({ value: le.id, label: le.name }));

  // Если вызывающий передал controlStyleOverrides — мержим их с
  // глобальным customStyles.control. Иначе — оставляем дефолт.
  const styles = useMemo(() => {
    if (!controlStyleOverrides) return customStyles;
    return {
      ...customStyles,
      control: (provided: Record<string, unknown>, state: unknown) => ({
        ...(customStyles.control
          ? customStyles.control(provided, state as never)
          : provided),
        ...controlStyleOverrides,
      }),
    };
  }, [controlStyleOverrides]);

  return (
    <ReactSelect
      styles={styles}
      loadingMessage={() => 'Загрузка...'}
      isLoading={legalEntitiesFetchedStatus === 'loading'}
      options={filteredIntegrators}
      value={selectedLegalEntity}
      onChange={setSelectedLegalEntity}
      placeholder="Юридические лица"
      isMulti={false}
      closeMenuOnSelect={true}
      hideSelectedOptions={false}
      isSearchable={true}
      isClearable={isClearable}
    />
  );
}

export default SelectLegalEntities;
